const DEFAULT_FETCH_TIMEOUT_MS = 20000;

export class FetchTimeoutError extends Error {
	constructor(timeoutMs: number) {
		super(`Request timed out after ${timeoutMs}ms`);
		this.name = "FetchTimeoutError";
	}
}

export class HttpError extends Error {
	readonly response: Response;
	readonly status: number;

	constructor(response: Response) {
		super(`Request failed with status ${response.status}`);
		this.name = "HttpError";
		this.response = response;
		this.status = response.status;
	}
}

type FetchWithTimeoutOptions = RequestInit & {
	timeoutMs?: number;
	throwOnHttpError?: boolean;
};

export async function fetchWithTimeout(
	input: RequestInfo | URL,
	{
		timeoutMs = DEFAULT_FETCH_TIMEOUT_MS,
		throwOnHttpError = true,
		signal,
		...init
	}: FetchWithTimeoutOptions = {},
): Promise<Response> {
	const requestController = new AbortController();
	let didTimeout = false;
	const abortFromCaller = () => requestController.abort(signal?.reason);

	if (signal?.aborted) {
		abortFromCaller();
	} else {
		signal?.addEventListener("abort", abortFromCaller, { once: true });
	}

	const timeoutId = setTimeout(() => {
		didTimeout = true;
		requestController.abort();
	}, timeoutMs);

	try {
		const response = await fetch(input, {
			...init,
			signal: requestController.signal,
		});

		if (throwOnHttpError && !response.ok) {
			throw new HttpError(response);
		}

		return response;
	} catch (error) {
		if (didTimeout) {
			throw new FetchTimeoutError(timeoutMs);
		}

		throw error;
	} finally {
		clearTimeout(timeoutId);
		signal?.removeEventListener("abort", abortFromCaller);
	}
}
